<template>
  <div class="media">
    <div v-if="showThumbnail && file" class="media-left" :class="mediaThumbnailClass">
      <div class="float-start wdw-thought-thumbnail">
        <a :href="file.url" target="_blank">
          <img class="img-thumbnail" role="presentation" :src="thoughtThumbnail" />
        </a>
      </div>
    </div>
    <div class="media-body">
      <div class="thought-content">
        <h3 class="thought-title">
          <span v-if="config.disableOpening" v-dompurify-html="thought.title"></span>
          <a
            v-else
            class="click-link"
            tabindex="0"
            :aria-label="thoughtMoreAriaLabel"
            @click="emitOpenThought(thought.id, thought.topic.id)"
            @keyup.enter="emitOpenThought(thought.id, thought.topic.id)"
            v-dompurify-html="thought.title"
            role="link"
          ></a>
        </h3>
        <div v-if="showAttributeTags" class="attribute-list">
          <thought-attribute-tags :attributes="attributesToDisplay" />
        </div>
        <div
          v-if="project.config && toBool(project.config.show_thought_themenbereich_icon) && themeCode"
          class="d-flex align-items-center mb-2 d-md-none flex-wrap"
        >
          <div v-for="value in themeCode.value_all" v-bind:key="value" class="p-1">
            <img
              :src="themeUrl + `/${value}.png`"
              class="img-fluid"
              style="width: 35px; height: 35px; min-width: 35px"
              alt=""
              @click="setFilterFromThought('attribute' + themeCode.id, value)"
            />
          </div>
        </div>
        <p class="">
          <span v-dompurify-html="thoughtContent"></span>
          <span v-if="thoughtShortened && !config.disableOpening">
            ...
            <a
              class="click-link"
              tabindex="0"
              :aria-label="thoughtMoreAriaLabel"
              @click="emitOpenThought(thought.id, thought.topic.id)"
              @keyup.enter="emitOpenThought(thought.id, thought.topic.id)"
              role="link"
            >
              mehr
            </a>
          </span>
        </p>
        <div v-if="!showAttributeTags" class="attribute-list">
          <attribute-show-list :attributes="attributesToDisplay" :compact="true" />
        </div>
        <div v-if="thoughtLastUpdated">
          <i>
            <small
              v-dompurify-html="'Letzte Aktualisierung: ' + formattedDate(UTCtoLocalTime(thoughtLastUpdated))"
            ></small>
          </i>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { domainStore } from '@/store/domain-store';
import { projectStore } from '@/store/project-store';
import { toRefs, computed } from 'vue';
import ThoughtEventMixins from '@/mixins/ThoughtEventMixins';
import useThoughtEvents from '@/composables/thought/useThoughtEvents';
import ThoughtAttributeTags from './AttributeTags.vue';
import AttributeShowList from '@/components/attribute/ShowList.vue';
import useConverter from '@/composables/general/useConverter';
import { filterStore } from '@/store/filter-store';
import { useRoute } from 'vue-router';

export default {
  name: 'ThoughtListContent',
  mixins: [ThoughtEventMixins],
  props: {
    thought: {
      type: Object,
      required: true,
    },
    topic: {
      type: Object,
      required: true,
    },
    config: {
      type: Object,
      default: () => ({}),
    },
  },

  setup(props, context) {
    //-----------------------------CONSTANTES-------------------------------
    const { thought, topic } = toRefs(props);
    const { emitOpenThought } = useThoughtEvents(context);
    const { formattedDate, toBool, UTCtoLocalTime } = useConverter();
    const thoughtShortened = thought.value.content.length > 140;
    const thoughtContent = thought.value.content.substring(0, 140);

    //---------------------------------COMPUTED-------------------------------
    const attributesToDisplay = computed(() => {
      const attributeList = [];
      topic.value.attributes.forEach(topic_attr => {
        thought.value.attributes.forEach(attr_val => {
          if (topic_attr.id === attr_val.id && topic_attr.relation_data.show_frontend_tag == 1) {
            attributeList.push(attr_val);
          }
        });
      });
      return attributeList;
    });
    const thoughtMoreAriaLabel = computed(() => {
      return domainStore.translate('thought.general.title') + ' ' + thought.value.number + ' ' + thought.value.title;
    });
    const project = computed(() => {
      return projectStore.getState().project;
    });

    const themeUrl = computed(() => {
      return project.value.config.thought_themenbereich_icon_url;
    });
    const projectThemeAtrribute = computed(() => {
      return project.value.config.themenbereich_attribute;
    });
    const themeCode = computed(() => {
      return props.thought.attributes.find(element => {
        return element.code == projectThemeAtrribute.value;
      });
    });
    const thoughtLastUpdated = computed(() => {
      return project.value.config?.show_thought_last_updated ? thought.value.last_update : false;
    });
    const showThumbnail = computed(() => {
      if (props.config.showThumbnail === false) {
        return false;
      }
      if (project.value.config?.thought_image_preview) {
        return project.value.config.thought_image_preview.includes('list');
      }
      return true;
    });
    const mediaThumbnailClass = computed(() => {
      if (project.value.config?.thought_image_preview_width) {
        const size = project.value.config.thought_image_preview_width;
        return size == 'sq256' ? 'media-width-256' : 'media-width-128';
      }
      return { 'media-width-256': true };
    });
    const showAttributeTags = computed(() => {
      if (project.value.config?.attribute_style != 'in_detail' || !project.value.config) {
        return true;
      } else {
        return false;
      }
    });
    const file = computed(() => {
      let fileWithThumb = null;

      for (const file of thought.value.files) {
        if (file.thumbnails && file.thumbnails.sq128) {
          fileWithThumb = file;
          break;
        }
      }
      return fileWithThumb;
    });

    const thoughtThumbnail = computed(() => {
      if (project.value.config?.thought_image_preview_width) {
        const size = project.value.config.thought_image_preview_width;
        return size == 'sq256' ? file.value.thumbnails.sq256 : file.value.thumbnails.sq128;
      } else {
        return file.value.thumbnails.sq256;
      }
    });

    // Sets key-value that is passed as filter to the filter form
    // Note: This is done through change in hash parameters
    function setFilterFromThought(key, value) {
      filterStore.setIndividualFilter({ key: key, value: value }, route, true);
    }

    return {
      //----------const-------------
      thoughtShortened,
      thoughtContent,
      //--------computed-----------
      thoughtMoreAriaLabel,
      attributesToDisplay,
      file,
      thoughtLastUpdated,
      showThumbnail,
      showAttributeTags,
      thoughtThumbnail,
      mediaThumbnailClass,
      themeCode,
      themeUrl,
      project,
      //---------emiter------------
      emitOpenThought,
      //---------Functions---------
      formattedDate,
      setFilterFromThought,
      toBool,
      UTCtoLocalTime,
    };
  },

  components: {
    ThoughtAttributeTags,
    AttributeShowList,
  },
};
</script>

<style scoped lang="scss">
a {
  cursor: pointer;
}
h3 {
  /* to be able to float text next to image, required for overruling customer styles */
  clear: none;
  &.thought-title {
    font-size: 1.25rem;
  }
}
</style>
